<template>
  <div>
    <!-- 操作按钮 -->
    <a-row v-if="!disableSubmit" style="margin-bottom: 10px">
      <a-col :span="24">
        <a-button @click="addItem" type="primary" icon="plus">新增</a-button>
        <a-popconfirm v-if="items.length > 0" title="确定删除当前记录吗?" @confirm="deleteCurrentItem" style="margin-left: 10px">
          <a-button type="danger" icon="delete">删除当前</a-button>
        </a-popconfirm>
      </a-col>
    </a-row>

    <!-- 内容区 -->
    <div v-if="items.length > 0" style="max-height: calc(100vh - 300px); overflow-y: auto; padding: 0 10px;">
      <div v-for="(item, idx) in items" v-show="idx === currentIndex" :key="item._key">
        <a-form layout="horizontal">
          <!-- 质控措施 -->
          <a-row :gutter="24">
            <a-col :md="12" :sm="24">
              <a-form-item :labelCol="labelCol" :wrapperCol="wrapperCol" label="质控措施">
                <a-select
                  v-model="item.qcMeasure"
                  placeholder="请选择质控措施"
                  :disabled="disableSubmit"
                  @change="(val) => onQcMeasureChange(item, val)"
                >
                  <a-select-option v-for="opt in filteredMeasureOptions" :key="opt.value" :value="opt.value">
                    {{ opt.text }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="12" :sm="24">
              <a-form-item :labelCol="labelCol" :wrapperCol="wrapperCol" label="核查因子">
                <a-select
                  v-model="item.factorCode"
                  placeholder="请选择核查因子"
                  :disabled="disableSubmit"
                  @change="(val) => onFactorChange(item, val)"
                >
                  <a-select-option v-for="f in getFactorOptions(item)" :key="f.code" :value="f.code">
                    {{ f.name }}（{{ f.unit }}）
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>

          <!-- 标液浓度 & 仪器量程 -->
          <a-row :gutter="24">
            <a-col :md="12" :sm="24">
              <a-form-item :labelCol="labelCol" :wrapperCol="wrapperCol" label="标液浓度">
                <a-input-number
                  v-model="item.standardConcentration"
                  :disabled="disableSubmit"
                  style="width: 80%"
                  @change="() => calcError(item)"
                />
                <span style="margin-left: 5px">{{ item.factorUnit || '' }}</span>
              </a-form-item>
            </a-col>
            <a-col :md="12" :sm="24">
              <a-form-item :labelCol="labelCol" :wrapperCol="wrapperCol" label="仪器量程">
                <a-input-number
                  v-model="item.instrumentRange"
                  :disabled="disableSubmit"
                  style="width: 80%"
                  @change="() => calcError(item)"
                />
                <span style="margin-left: 5px">{{ item.factorUnit || '' }}</span>
              </a-form-item>
            </a-col>
          </a-row>

          <!-- 上次值（仅漂移类显示） -->
          <a-row :gutter="24" v-if="isDriftMeasure(item.qcMeasure)">
            <a-col :md="12" :sm="24">
              <a-form-item :labelCol="labelCol" :wrapperCol="wrapperCol" label="上次仪器测定值">
                <a-input-number
                  v-model="item.lastMeasuredValue"
                  :disabled="disableSubmit"
                  style="width: 80%"
                  @change="() => calcError(item)"
                />
                <span style="margin-left: 5px">{{ item.factorUnit || '' }}</span>
              </a-form-item>
            </a-col>
            <a-col :md="12" :sm="24">
              <a-form-item :labelCol="labelCol" :wrapperCol="wrapperCol" label="上次测量时间">
                <a-date-picker
                  style="width: 100%"
                  format="YYYY-MM-DD HH:mm"
                  :show-time="{ format: 'HH:mm' }"
                  v-model="item.lastMeasureTime"
                  :disabled="disableSubmit"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <!-- 本次测定值 & 测量时间 -->
          <a-row :gutter="24">
            <a-col :md="12" :sm="24">
              <a-form-item :labelCol="labelCol" :wrapperCol="wrapperCol" label="本次仪器测定值">
                <a-input-number
                  v-model="item.currentMeasuredValue"
                  :disabled="disableSubmit"
                  style="width: 80%"
                  @change="() => calcError(item)"
                />
                <span style="margin-left: 5px">{{ item.factorUnit || '' }}</span>
              </a-form-item>
            </a-col>
            <a-col :md="12" :sm="24">
              <a-form-item :labelCol="labelCol" :wrapperCol="wrapperCol" label="本次测量时间">
                <a-date-picker
                  style="width: 100%"
                  format="YYYY-MM-DD HH:mm"
                  :show-time="{ format: 'HH:mm' }"
                  v-model="item.currentMeasureTime"
                  :disabled="disableSubmit"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <!-- 误差 & 合格 -->
          <a-row :gutter="24">
            <a-col :md="12" :sm="24" v-if="showAbsoluteError(item)">
              <a-form-item :labelCol="labelCol" :wrapperCol="wrapperCol" label="绝对误差">
                <span>{{ item.absoluteError != null ? item.absoluteError : '--' }} {{ item.factorUnit || '' }}</span>
              </a-form-item>
            </a-col>
            <a-col :md="12" :sm="24" v-if="showRelativeError(item)">
              <a-form-item :labelCol="labelCol" :wrapperCol="wrapperCol" label="相对误差">
                <span>{{ item.relativeError != null ? item.relativeError + '%' : '--' }}</span>
              </a-form-item>
            </a-col>
          </a-row>

          <a-row :gutter="24">
            <a-col :md="12" :sm="24">
              <a-form-item :labelCol="labelCol" :wrapperCol="wrapperCol" label="是否合格">
                <a-select v-model="item.qualified" :disabled="disableSubmit" placeholder="请选择">
                  <a-select-option value="yes">合格</a-select-option>
                  <a-select-option value="no">不合格</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>

          <!-- 备注 -->
          <a-row :gutter="24">
            <a-col :md="24" :sm="24">
              <a-form-item :labelCol="{ span: 4 }" :wrapperCol="{ span: 20 }" label="备注">
                <a-textarea v-model="item.remark" :disabled="disableSubmit" placeholder="请输入备注" />
              </a-form-item>
            </a-col>
          </a-row>

          <!-- 子表附件（每条记录独立） -->
          <a-row style="margin-top: 5px">
            <a-col>

              <iws-upload
                :ref="'itemUpload_' + idx"
                :options="{ btnText: '上传附件' }"
                :disableSubmit="disableSubmit"
                filesrc="om_qc_st21_item"
                :srcid="item.id"
              >
              </iws-upload>
            </a-col>
          </a-row>
        </a-form>
      </div>
    </div>

    <div v-else style="text-align: center; color: #999; padding: 80px 0;">
      暂无质控记录，请点击「新增」按钮添加
    </div>

    <!-- 翻页器 -->
    <div v-if="items.length > 0" style="text-align: center; padding: 10px 0;">
      <a-button :disabled="currentIndex <= 0" @click="currentIndex--" icon="left"></a-button>
      <span style="margin: 0 15px; font-size: 14px;">{{ currentIndex + 1 }} / {{ items.length }}</span>
      <a-button :disabled="currentIndex >= items.length - 1" @click="currentIndex++" icon="right"></a-button>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { getAction, deleteAction } from '@/api/manage'
import { ajaxGetDictItems } from '@/api/api'
import IwsUpload from '@comp/upload/IwsUpload.vue'

export default {
  name: 'St21WeekForm',
  components: { IwsUpload },
  props: {
    disableSubmit: { type: Boolean, default: false },
    siteId: { type: [Number, String], default: null },
    st: { type: String, default: '21' },
    maintenanceType: { type: String, default: '' },
    qcMeasure: { type: String, default: undefined },
    pagePath: { type: String, default: '/om/qc/ledger' },
    dataItems: { type: Array, default: () => [] },
  },
  data() {
    return {
      items: [],
      currentIndex: 0,
      measureOptions: [],
      factorWhitelistCache: {},
      siteFactors: [],
      labelCol: { span: 8 },
      wrapperCol: { span: 16 },
      keyCounter: 0,
    }
  },
  computed: {
    /** 日常质控模式 */
    isDailyMode() {
      return this.maintenanceType === 'daily'
    },
    /** 根据维护类型过滤措施选项 */
    filteredMeasureOptions() {
      return this.measureOptions.filter((item) => {
        let types = (item.depict || '').split(',')
        return types.includes(this.maintenanceType)
      })
    },
  },
  watch: {
    dataItems: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0) {
          this.items = val.map((item) => {
            let mapped = { ...item, _key: 'k_' + this.keyCounter++ }
            if (mapped.lastMeasureTime) mapped.lastMeasureTime = moment(mapped.lastMeasureTime)
            if (mapped.currentMeasureTime) mapped.currentMeasureTime = moment(mapped.currentMeasureTime)
            return mapped
          })
          this.currentIndex = 0
        }
      },
    },
    maintenanceType: {
      immediate: true,
      handler() {
        this.loadMeasureDict()
      },
    },
    siteId(val) {
      if (val) {
        // siteId 变化时清空因子白名单缓存并重新加载
        this.factorWhitelistCache = {}
      }
    },
  },
  mounted() {
    // 新增模式下自动添加第一条记录
    if (!this.disableSubmit && this.items.length === 0 && (!this.dataItems || this.dataItems.length === 0)) {
      this.addItem()
    }
  },
  created() {
    this.loadMeasureDict()
  },
  methods: {
    /** 加载质控措施字典 */
    loadMeasureDict() {
      if (!this.st) return
      ajaxGetDictItems('qc_measure_st' + this.st).then((res) => {
        if (res.success) {
          this.measureOptions = res.result || []
        }
      })
    },
    /** 新增一条记录 */
    addItem() {
      let newItem = {
        _key: 'k_' + this.keyCounter++,
        id: null,
        qcMeasure: undefined,
        factorCode: undefined,
        factorName: '',
        factorUnit: '',
        standardConcentration: null,
        instrumentRange: null,
        currentMeasuredValue: null,
        currentMeasureTime: null,
        absoluteError: null,
        relativeError: null,
        qualified: undefined,
        lastMeasuredValue: null,
        lastMeasureTime: null,
        remark: '',
      }
      this.items.push(newItem)
      this.currentIndex = this.items.length - 1
    },
    /** 删除当前记录 */
    deleteCurrentItem() {
      let item = this.items[this.currentIndex]
      // 已持久化的记录调后端删除
      if (item.id) {
        deleteAction(this.pagePath + '/item', { id: item.id, st: this.st }).then(() => {})
      }
      this.items.splice(this.currentIndex, 1)
      if (this.currentIndex >= this.items.length) {
        this.currentIndex = Math.max(0, this.items.length - 1)
      }
    },
    /** 质控措施变更 */
    onQcMeasureChange(item, val) {
      item.qcMeasure = val
      // 不再直接清除 factorCode，而是等到 whitelist 加载后或直接使用已缓存的验证
      let cacheKey = this.st + '_' + val
      let whitelist = this.factorWhitelistCache[cacheKey]
      if (whitelist && whitelist.length > 0) {
        // 如果已经有缓存，直接判断该核查因子是否适用这个质控措施
        let found = whitelist.find(f => f.code === item.factorCode)
        if (!found) {
          item.factorCode = undefined
          item.factorName = ''
          item.factorUnit = ''
        }
      }

      item.absoluteError = null
      item.relativeError = null
      item.lastMeasuredValue = null
      item.lastMeasureTime = null
    },
    /** 核查因子变更 */
    onFactorChange(item, code) {
      let factors = this.getFactorOptions(item)
      let found = factors.find((f) => f.code === code)
      if (found) {
        item.factorCode = found.code
        item.factorName = found.name
        item.factorUnit = found.unit
      }
      // 漂移类自动回填上次值
      if (this.isDriftMeasure(item.qcMeasure)) {
        this.loadLastRecord(item)
      }
      this.calcError(item)
    },
    /** 获取可选因子列表（白名单 ∩ 站点因子） */
    getFactorOptions(item) {
      if (!item.qcMeasure) return []
      let cacheKey = this.st + '_' + item.qcMeasure
      let whitelist = this.factorWhitelistCache[cacheKey]
      if (!whitelist) {
        // 先设为空数组防止无限循环渲染
        this.$set(this.factorWhitelistCache, cacheKey, [])
        // 异步加载
        this.loadFactorWhitelist(item.qcMeasure, cacheKey)
        return this.factorWhitelistCache[cacheKey]
      }
      return whitelist
    },
    /** 加载因子白名单 */
    loadFactorWhitelist(qcMeasure, cacheKey) {
      if (!this.siteId) return
      Promise.all([
        getAction(this.pagePath + '/factorWhitelist', { st: this.st, qcMeasure: qcMeasure }),
        getAction('/site/info/factor/find/' + this.siteId)
      ]).then(([resWhite, resSite]) => {
        if (resWhite.success && resWhite.result) {
          let siteFactors = (resSite.success && resSite.result) ? resSite.result : []
          let factorList = []
          resWhite.result.forEach((code) => {
            let f = siteFactors.find((sf) => sf.code === code)
            if (f) factorList.push({ code: code, name: f.title, unit: f.unitName || '' })
          })
          this.$set(this.factorWhitelistCache, cacheKey, factorList)
          
          // 异步加载完后，验证所有使用了该 qcMeasure 的 item
          this.items.forEach(item => {
            if (item.qcMeasure === qcMeasure && item.factorCode) {
              let found = factorList.find(f => f.code === item.factorCode)
              if (!found) {
                item.factorCode = undefined
                item.factorName = ''
                item.factorUnit = ''
              }
            }
          })
        }
      })
    },
    /** 是否为漂移类措施 */
    isDriftMeasure(measure) {
      return measure === 'zero_drift' || measure === 'span_drift'
    },
    /** 是否显示绝对误差 */
    showAbsoluteError(item) {
      // 电导率且标液浓度 > 100 时，不显示绝对误差
      if (item.factorCode === 'w01014' && item.standardConcentration != null && item.standardConcentration > 100) {
        return false
      }
      return true
    },
    /** 需要同时显示绝对误差和相对误差的因子 */
    isBothErrorFactor(factorCode) {
      // 浊度w01003、高锰酸盐指数w01019、氨氮w21003、总磷w21011、总氮w21001
      return ['w01003', 'w01019', 'w21003', 'w21011', 'w21001'].includes(factorCode)
    },
    /** 是否显示相对误差 */
    showRelativeError(item) {
      let m = item.qcMeasure
      if (m === 'zero_drift' || m === 'span_drift' || m === 'span_check') return true
      // 浊度、高锰酸盐指数、氨氮、总磷、总氮：始终显示相对误差
      if (this.isBothErrorFactor(item.factorCode)) return true
      // 电导率特殊规则：标液浓度 > 100 显示相对误差，≤ 100 不显示
      if (item.factorCode === 'w01014') {
        return item.standardConcentration != null && item.standardConcentration > 100
      }
      return false
    },
    /** 计算误差 */
    calcError(item) {
      let m = item.qcMeasure
      // 绝对误差
      if (this.isDriftMeasure(m)) {
        if (item.currentMeasuredValue != null && item.lastMeasuredValue != null) {
          item.absoluteError = this.fixFloat(item.currentMeasuredValue - item.lastMeasuredValue)
        } else {
          item.absoluteError = null
        }
      } else {
        if (item.currentMeasuredValue != null && item.standardConcentration != null) {
          item.absoluteError = this.fixFloat(item.currentMeasuredValue - item.standardConcentration)
        } else {
          item.absoluteError = null
        }
      }
      // 相对误差（截断两位小数）
      if (this.showRelativeError(item) && item.absoluteError != null && item.standardConcentration && item.standardConcentration !== 0) {
        item.relativeError = this.truncate2((item.absoluteError / item.standardConcentration) * 100)
      } else {
        item.relativeError = null
      }
    },
    /** 修正浮点精度（保留6位有效小数） */
    fixFloat(val) {
      if (val == null) return null
      return parseFloat(val.toFixed(6))
    },
    /** 截断两位小数（非四舍五入）*/
    truncate2(val) {
      if (val == null) return null
      return Math.trunc(this.fixFloat(val) * 100) / 100
    },
    /** 自动回填上次值 */
    loadLastRecord(item) {
      if (!this.siteId || !item.qcMeasure || !item.factorCode) return
      getAction(this.pagePath + '/lastRecord', {
        siteId: this.siteId,
        maintenanceType: this.maintenanceType,
        qcMeasure: item.qcMeasure,
        factorCode: item.factorCode,
      }).then((res) => {
        if (res.success && res.result) {
          item.lastMeasuredValue = res.result.currentMeasuredValue
          item.lastMeasureTime = res.result.currentMeasureTime
        }
      })
    },
    /** 收集数据给Drawer提交（含校验） */
    collectData() {
      if (this.items.length === 0) {
        this.$message.warning('请至少添加一条质控记录')
        return null
      }
      for (let i = 0; i < this.items.length; i++) {
        let item = this.items[i]
        if (!item.qcMeasure) {
          this.$message.warning(`第 ${i + 1} 条记录未选择质控措施`)
          this.currentIndex = i
          return null
        }
        if (!item.factorCode) {
          this.$message.warning(`第 ${i + 1} 条记录未选择核查因子`)
          this.currentIndex = i
          return null
        }
        // 收集附件数据（非必填）
        let uploadRef = this.$refs['itemUpload_' + i]
        if (uploadRef && uploadRef[0]) {
          let fileData = uploadRef[0].getUploadList ? uploadRef[0].getUploadList() : {}
          item.sysFileList = fileData.sysFileList || []
          item.fileDelList = fileData.fileDelList || []
        }
      }
      // 清理临时字段
      return this.items.map((item) => {
        let { _key, ...data } = item
        return data
      })
    },
  },
}
</script>
